/**
 * VideoReader  — seek-based frame extraction (Pass 1) + real-time render (Pass 2)
 * VideoWriter  — MediaRecorder wrapping canvas video + optional audio from source element
 */
export class VideoReader {
  constructor() {
    this.video = document.createElement('video');
    this.video.muted = true;
    this.video.playsInline = true;
    this._canvas = document.createElement('canvas');
    this._ctx = this._canvas.getContext('2d', { willReadFrequently: true });
    this._objectUrl = null;
    this._fps = 30;
  }

  async load(file) {
    if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);
    this._objectUrl = URL.createObjectURL(file);

    await new Promise((resolve, reject) => {
      this.video.onloadedmetadata = resolve;
      this.video.onerror = () => reject(new Error('Failed to load video'));
      this.video.src = this._objectUrl;
    });

    // Some files (QuickTime recordings, screen captures) report Infinity until
    // we seek past the end, which forces the browser to read the full moov atom.
    if (!isFinite(this.video.duration)) {
      await new Promise(resolve => {
        const onDurationChange = () => {
          if (isFinite(this.video.duration)) {
            this.video.removeEventListener('durationchange', onDurationChange);
            resolve();
          }
        };
        this.video.addEventListener('durationchange', onDurationChange);
        this.video.currentTime = 1e9;
        setTimeout(resolve, 3000); // fallback if duration never resolves
      });
      await this._seekTo(0);
    }

    this._canvas.width  = this.video.videoWidth;
    this._canvas.height = this.video.videoHeight;
    this._fps = await detectFps(this.video) ?? 30;
  }

  get width()      { return this.video.videoWidth; }
  get height()     { return this.video.videoHeight; }
  get duration()   { return this.video.duration; }
  get fps()        { return this._fps; }
  get frameCount() { return Math.ceil(this.duration * this._fps); }

  // ── On-demand frame fetch (review mode) ─────────────────────────────────

  async getFrameAt(frameIndex) {
    const t = frameIndex / this._fps;
    await this._seekTo(Math.min(t, this.duration - 0.001));
    this._ctx.drawImage(this.video, 0, 0);
    return this._ctx.getImageData(0, 0, this.width, this.height);
  }

  // ── Pass 1: seek through every frame ────────────────────────────────────

  async *frames() {
    const step = 1 / this._fps;
    let prevTime = -1;
    for (let t = 0; t < this.duration - step / 2; t += step) {
      await this._seekTo(t);
      // Stop if seeking no longer advances (reached actual end on Infinity-duration files)
      if (this.video.currentTime <= prevTime + step * 0.25) break;
      prevTime = this.video.currentTime;
      this._ctx.drawImage(this.video, 0, 0);
      yield this._ctx.getImageData(0, 0, this.width, this.height);
    }
  }

  _seekTo(time) {
    return new Promise(resolve => {
      const h = () => { this.video.removeEventListener('seeked', h); resolve(); };
      this.video.addEventListener('seeked', h);
      this.video.currentTime = time;
    });
  }

  // ── Pass 2: real-time playback with pre-computed detections ─────────────

  /**
   * Play the video at 1× speed. For each frame, look up pre-computed
   * detections, apply blur, and call onFrame with the result.
   * Audio streams naturally from the video element into VideoWriter.
   *
   * @param {Map<number, Detection[]>} detectionMap  frameIndex → boxes
   * @param {object}  blurrer
   * @param {object}  opts
   * @param {function} onFrame     (ImageData) => void
   * @param {function} onProgress  (index, total) => void
   */
  async playAndRender(detectionMap, blurrer, opts, onFrame, onProgress) {
    await this._seekTo(0);
    const total = this.frameCount;

    return new Promise((resolve, reject) => {
      // Idempotent — safe to call multiple times
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };

      // Safety timeout: if nothing resolves within duration + 30 s, unstick
      const safetyTimer = setTimeout(done, (this.duration + 30) * 1000);
      const finish = () => { clearTimeout(safetyTimer); done(); };

      const processFrame = async (_now, metadata) => {
        try {
          const frameIdx = Math.min(
            Math.round(metadata.mediaTime * this._fps),
            total - 1,
          );

          this._ctx.drawImage(this.video, 0, 0);
          const raw        = this._ctx.getImageData(0, 0, this.width, this.height);
          const detections = detectionMap.get(frameIdx) ?? [];

          let processed = raw;
          if (detections.length > 0) {
            try { processed = await blurrer.blurFaces(raw, detections, opts); }
            catch (e) { console.warn('[render] blurFaces failed, using raw frame:', e); }
          }

          onFrame(processed, detections, frameIdx);
          onProgress?.(frameIdx, total);

          // Explicit last-frame detection as primary completion signal
          if (frameIdx >= total - 1) { finish(); return; }
        } catch (e) {
          console.error('[render] processFrame error:', e);
        }

        if (!settled) {
          if (!this.video.ended && !this.video.paused) {
            this.video.requestVideoFrameCallback(processFrame);
          } else {
            finish();
          }
        }
      };

      // Firefox fallback (no requestVideoFrameCallback)
      if (!('requestVideoFrameCallback' in this.video)) {
        let lastIdx = -1;
        let processing = false;
        const onTimeUpdate = async () => {
          if (settled || processing) return;
          const frameIdx = Math.min(
            Math.round(this.video.currentTime * this._fps), total - 1,
          );
          if (frameIdx === lastIdx) return;
          lastIdx = frameIdx;
          processing = true;
          try {
            this._ctx.drawImage(this.video, 0, 0);
            const raw = this._ctx.getImageData(0, 0, this.width, this.height);
            const detections = detectionMap.get(frameIdx) ?? [];
            let processed = raw;
            if (detections.length > 0) {
              try { processed = await blurrer.blurFaces(raw, detections, opts); }
              catch (e) { console.warn('[render/ff] blurFaces failed:', e); }
            }
            onFrame(processed, detections, frameIdx);
            onProgress?.(frameIdx, total);
            if (frameIdx >= total - 1) finish();
          } catch (e) { console.error('[render/ff] error:', e); }
          processing = false;
        };
        this.video.addEventListener('timeupdate', onTimeUpdate);
        this.video.addEventListener('ended', finish, { once: true });
        this.video.play().catch(err => { clearTimeout(safetyTimer); reject(err); });
        return;
      }

      this.video.requestVideoFrameCallback(processFrame);
      this.video.addEventListener('ended', finish, { once: true });
      this.video.play().catch(err => { clearTimeout(safetyTimer); reject(err); });
    });
  }

  dispose() {
    if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);
  }
}

// ── VideoWriter ─────────────────────────────────────────────────────────────

/**
 * Records processed frames from canvas + original audio from sourceVideo.
 * Uses MediaRecorder (WebM) when captureStream is available, WebCodecs (MP4)
 * as a fallback for iOS Safari where captureStream is not supported.
 */
export class VideoWriter {
  constructor(canvas, fps, sourceVideo = null) {
    if ('captureStream' in HTMLCanvasElement.prototype) {
      this._impl = new MediaRecorderWriter(canvas, fps, sourceVideo);
    } else {
      this._impl = new WebCodecsWriter(canvas, fps);
    }
  }

  start()              { return this._impl.start(); }
  writeFrame(img)      { return this._impl.writeFrame(img); }
  stop()               { return this._impl.stop(); }
}

// ── MediaRecorder path (Chrome, Firefox, desktop Safari) ────────────────────

class MediaRecorderWriter {
  constructor(canvas, fps, sourceVideo) {
    this.canvas  = canvas;
    this._chunks = [];

    const videoTracks = canvas.captureStream(fps).getVideoTracks();

    let audioTracks = [];
    try {
      if (sourceVideo?.captureStream) {
        audioTracks = sourceVideo.captureStream().getAudioTracks();
      }
    } catch { /* ignore */ }

    const stream   = new MediaStream([...videoTracks, ...audioTracks]);
    const mimeType = pickMimeType(audioTracks.length > 0);
    this._recorder = new MediaRecorder(stream, { mimeType });
    this._recorder.ondataavailable = e => { if (e.data.size) this._chunks.push(e.data); };
  }

  start() { this._recorder.start(100); }

  writeFrame(imageData) {
    this.canvas.getContext('2d').putImageData(imageData, 0, 0);
  }

  stop() {
    return new Promise(resolve => {
      this._recorder.onstop = () => {
        const blob = new Blob(this._chunks, { type: this._recorder.mimeType });
        resolve(URL.createObjectURL(blob));
      };
      this._recorder.stop();
    });
  }
}

// ── WebCodecs path (iOS Safari) ─────────────────────────────────────────────

class WebCodecsWriter {
  constructor(canvas, fps) {
    this.canvas      = canvas;
    this.fps         = fps;
    this._frameIndex = 0;
    this._ctx        = canvas.getContext('2d');
  }

  async start() {
    const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');
    this._target = new ArrayBufferTarget();
    this._muxer  = new Muxer({
      target:     this._target,
      video:      { codec: 'avc', width: this.canvas.width, height: this.canvas.height },
      fastStart:  'in-memory',
    });

    this._encoder = new VideoEncoder({
      output: (chunk, meta) => this._muxer.addVideoChunk(chunk, meta),
      error:  e => console.error('[WebCodecs]', e),
    });
    this._encoder.configure({
      codec:     'avc1.42001f',
      width:     this.canvas.width,
      height:    this.canvas.height,
      bitrate:   4_000_000,
      framerate: this.fps,
    });
  }

  writeFrame(imageData) {
    this._ctx.putImageData(imageData, 0, 0);
    const timestamp = Math.round((this._frameIndex / this.fps) * 1_000_000);
    const keyFrame  = this._frameIndex % Math.round(this.fps * 2) === 0;
    const frame = new VideoFrame(this.canvas, { timestamp });
    this._encoder.encode(frame, { keyFrame });
    frame.close();
    this._frameIndex++;
  }

  async stop() {
    await this._encoder.flush();
    this._muxer.finalize();
    const blob = new Blob([this._target.buffer], { type: 'video/mp4' });
    return URL.createObjectURL(blob);
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────

async function detectFps(video) {
  try {
    if ('requestVideoFrameCallback' in video) {
      return await new Promise(resolve => {
        let prev  = null;
        let count = 0;
        const diffs = [];
        const cb = (_now, meta) => {
          if (prev !== null) diffs.push(meta.mediaTime - prev);
          prev = meta.mediaTime;
          if (++count < 8) video.requestVideoFrameCallback(cb);
          else {
            const avg = diffs.reduce((a, b) => a + b, 0) / diffs.length;
            resolve(avg > 0 ? Math.round(1 / avg) : null);
          }
        };
        video.requestVideoFrameCallback(cb);
        video.play().catch(() => {});
        setTimeout(() => { video.pause(); resolve(null); }, 2000);
      });
    }
  } catch { /* ignore */ }
  return null;
}

function pickMimeType(hasAudio = false) {
  const candidates = hasAudio
    ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
    : ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  return candidates.find(m => MediaRecorder.isTypeSupported(m)) ?? 'video/webm';
}
